import { createContext, useContext, useState, useEffect } from 'react'

const ClientAuthContext = createContext(null)

import { API_BASE_URL } from '../configs/config'

// Parse a JSON response, but say what actually came back when it isn't JSON
// (e.g. an empty 500 from the Vite proxy when the API it points at is down).
async function readJson(response) {
  const text = await response.text()
  try {
    return JSON.parse(text)
  } catch (_) {
    const what = text.trim() ? 'a non-JSON response' : 'an empty response'
    throw new Error(`API returned ${what} (HTTP ${response.status}) from ${response.url} — check VITE_API_BASE_URL and that the API is up`)
  }
}

export function ClientAuthProvider({ children }) {
  const [client, setClient] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(null)

  // On mount, check localStorage for existing token and client
  useEffect(() => {
    const storedToken = localStorage.getItem('clientToken')
    const storedClient = localStorage.getItem('clientData')

    if (storedToken && storedClient) {
      try {
        const parsed = JSON.parse(storedClient)
        setToken(storedToken)
        setClient(parsed)
        console.log('Client loaded from localStorage:', parsed.name)
      } catch (e) {
        setClient(null)
        setToken(null)
        localStorage.removeItem('clientToken')
        localStorage.removeItem('clientData')
      }
    } else {
      setClient(null)
      setToken(null)
    }
    setLoading(false)
  }, [])

  // Login function
  const login = async (username, password) => {
    console.log('Client login attempt for:', username)

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      })

      const data = await readJson(response)

      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || `Login failed (${response.status})`
        }
      }

      // Store token and client data
      localStorage.setItem('clientToken', data.token)
      localStorage.setItem('clientData', JSON.stringify(data.client))
      setToken(data.token)
      setClient(data.client)

      return { success: true }
    } catch (error) {
      console.error('Client login error:', error)
      return { success: false, error: error.message || 'Network error' }
    }
  }

  // Logout function
  const logout = () => {
    setClient(null)
    setToken(null)
    localStorage.removeItem('clientToken')
    localStorage.removeItem('clientData')
    console.log('Client logged out')
  }

  // Refresh client data from server
  const refreshClient = async () => {
    if (!token) return

    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await readJson(response)
        if (data.success) {
          // /me sends branding next to the client; keep it on the stored client
          const updated = { ...data.client, branding: data.branding ?? data.client?.branding ?? null }
          setClient(updated)
          localStorage.setItem('clientData', JSON.stringify(updated))
        }
      } else if (response.status === 401) {
        // Token expired or invalid
        logout()
      }
    } catch (error) {
      console.error('Failed to refresh client data:', error)
    }
  }

  // API helper function with auth header
  // Set returnBlob to true for binary responses (e.g., PDF downloads)
  const clientApi = async (endpoint, options = {}, returnBlob = false) => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }

    // Only set Content-Type for non-blob requests
    if (!returnBlob) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    })

    if (response.status === 401) {
      logout()
      throw new Error('Session expired')
    }

    // Return blob for binary responses (PDF, etc.)
    if (returnBlob) {
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }
      return response.blob()
    }

    return readJson(response)
  }

  const isAuthenticated = !!client && !!token

  // Update client data locally (for SSE updates)
  const updateClient = (updates) => {
    setClient(prev => {
      const updated = { ...prev, ...updates }
      localStorage.setItem('clientData', JSON.stringify(updated))
      return updated
    })
  }

  const value = {
    client,
    setClient: updateClient,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshClient,
    clientApi
  }

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  )
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext)
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider')
  }
  return context
}
